import { useTranslation } from "react-i18next";
import type { AdminAppLog } from "@/features/admin/admin.types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

type AdminLogViewerProps = {
  logs: AdminAppLog[];
  onAcknowledge: (logId: string) => void;
};

export function AdminLogViewer({ logs, onAcknowledge }: AdminLogViewerProps) {
  const { i18n, t } = useTranslation();

  if (!logs.length) {
    return (
      <div className="rounded-[1.6rem] border border-dashed border-white/10 bg-black/20 px-5 py-10 text-sm text-[#d7cab9]/72">
        {t("adminWorkspace.logs.empty", { defaultValue: "No log entries found." })}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {logs.map((log) => (
        <article
          className="rounded-[1.6rem] border border-white/10 bg-black/20 p-5"
          key={log.id}
        >
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">
                  {t(`adminWorkspace.logs.severity.${log.severity}`, { defaultValue: log.severity })}
                </Badge>
                <Badge variant="outline">{log.source}</Badge>
                {log.quotaResourceKind ? (
                  <Badge variant="outline">
                    {log.quotaResourceKind.replaceAll("_", " ")}
                  </Badge>
                ) : null}
                {log.alertStage ? (
                  <Badge variant="outline">{log.alertStage}</Badge>
                ) : null}
                {log.reviewStatus ? (
                  <Badge variant="outline">{log.reviewStatus}</Badge>
                ) : null}
                {log.acknowledged ? (
                  <Badge variant="outline">
                    {t("adminWorkspace.logs.acknowledged", { defaultValue: "Acknowledged" })}
                  </Badge>
                ) : null}
              </div>
              <h3 className="text-lg font-semibold text-[#fff7ef]">{log.message}</h3>
              <p className="text-xs text-[#d8c8b3]/65">
                {new Intl.DateTimeFormat(i18n.language, {
                  dateStyle: "medium",
                  timeStyle: "short",
                }).format(new Date(log.createdAt))}
              </p>
              {log.quotaResourceKind && typeof log.quotaObservedPercent === "number" ? (
                <p className="text-xs text-[#d8c8b3]/65">
                  {`Observed ${log.quotaObservedPercent.toFixed(2)}% of ${log.quotaResourceKind.replaceAll("_", " ")}${
                    typeof log.quotaThresholdPercent === "number"
                      ? ` (threshold ${log.quotaThresholdPercent}%)`
                      : ""
                  }.`}
                </p>
              ) : null}
              {log.flaggedUserId ? (
                <p className="text-xs text-[#d8c8b3]/65">
                  {`User: ${log.flaggedUserId}`}
                  {typeof log.usageMultiplier === "number"
                    ? ` | Usage multiplier: ${log.usageMultiplier.toFixed(2)}x`
                    : ""}
                </p>
              ) : null}
            </div>

            <Button
              disabled={log.acknowledged}
              size="sm"
              type="button"
              variant="outline"
              onClick={() => onAcknowledge(log.id)}
            >
              {log.acknowledged
                ? t("adminWorkspace.logs.acknowledged", { defaultValue: "Acknowledged" })
                : t("adminWorkspace.logs.acknowledge", { defaultValue: "Acknowledge" })}
            </Button>
          </div>

          <pre className="mt-4 overflow-x-auto rounded-[1.2rem] border border-white/8 bg-[#0f0d0b] px-4 py-4 text-xs leading-6 text-[#d8c8b3]/78">
            {JSON.stringify(log.metadata ?? {}, null, 2)}
          </pre>
        </article>
      ))}
    </div>
  );
}
